
import { useState } from 'react';
import {
  AlertTriangle,
  Ban,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  ExternalLink,
  HelpCircle,
  Lightbulb,
  UserCheck,
  XCircle,
} from 'lucide-react';
import {
  getXatTicketsGuideData,
  PAID_REQUIREMENT_LABELS,
  type PaidRequirement,
  type TicketCallout,
  type TicketDepartment,
  type TicketExample,
  type TicketFaqItem,
  type TicketPracticeCard,
} from '@/lib/xat-tickets-data';
import { copyText } from '@/components/help-center/latest-power-shared';
import { cn } from '@/lib/utils';

const PAID_PANEL_STYLES: Record<PaidRequirement, { wrap: string; label: string; value: string; note: string }> = {
  yes: {
    wrap: 'border-amber-200 bg-amber-50',
    label: 'text-amber-700',
    value: 'text-amber-900',
    note: 'text-amber-800/80',
  },
  no: {
    wrap: 'border-emerald-200 bg-emerald-50',
    label: 'text-emerald-700',
    value: 'text-emerald-900',
    note: 'text-emerald-800/80',
  },
  depends: {
    wrap: 'border-blue-200 bg-blue-50',
    label: 'text-blue-700',
    value: 'text-blue-900',
    note: 'text-blue-800/80',
  },
};

function TicketTipCard({ callout }: { callout: TicketCallout }) {
  const styles = {
    info: {
      icon: HelpCircle,
      iconWrap: 'bg-sky-100 text-sky-700',
      titleColor: 'text-sky-900',
      card: 'border-sky-100 bg-gradient-to-br from-sky-50/90 to-white',
    },
    tip: {
      icon: Lightbulb,
      iconWrap: 'bg-emerald-100 text-emerald-700',
      titleColor: 'text-emerald-900',
      card: 'border-emerald-100 bg-gradient-to-br from-emerald-50/90 to-white',
    },
    warning: {
      icon: AlertTriangle,
      iconWrap: 'bg-amber-100 text-amber-700',
      titleColor: 'text-amber-900',
      card: 'border-amber-100 bg-gradient-to-br from-amber-50/90 to-white',
    },
    success: {
      icon: CheckCircle2,
      iconWrap: 'bg-emerald-100 text-emerald-700',
      titleColor: 'text-emerald-900',
      card: 'border-emerald-100 bg-gradient-to-br from-emerald-50/90 to-white',
    },
  }[callout.type];

  const Icon = styles.icon;
  const defaultTitle =
    callout.type === 'tip'
      ? 'Dica'
      : callout.type === 'warning'
        ? 'Atenção'
        : callout.type === 'success'
          ? 'Resolvido'
          : 'Informação';

  return (
    <div className={cn('flex h-full flex-col rounded-2xl border p-5 shadow-sm', styles.card)}>
      <div className="mb-3 flex items-center gap-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
            styles.iconWrap
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </div>
        <h4 className={cn('font-poppins text-base font-bold leading-snug', styles.titleColor)}>
          {callout.title || defaultTitle}
        </h4>
      </div>
      <p className="font-inter text-sm leading-relaxed text-gray-600 md:text-[15px]">{callout.content}</p>
    </div>
  );
}

function DepartmentCard({ department }: { department: TicketDepartment }) {
  const paidPanel = PAID_PANEL_STYLES[department.paidRequired];

  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md md:p-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="mb-3 font-poppins text-lg font-bold leading-snug text-gray-900">{department.name}</h3>

          <p className="mb-4 font-inter text-[15px] leading-relaxed text-gray-600">{department.description}</p>

          {department.examples.length > 0 ? (
            <div>
              <p className="mb-2.5 font-inter text-xs font-semibold uppercase tracking-wide text-gray-400">
                Exemplos de uso
              </p>
              <ul className="grid gap-2 sm:grid-cols-2">
                {department.examples.map((ex) => (
                  <li key={ex} className="flex gap-2.5 font-inter text-sm leading-relaxed text-gray-700">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" aria-hidden />
                    <span>{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className={cn('shrink-0 rounded-xl border p-4 lg:w-56', paidPanel.wrap)}>
          <p className={cn('font-inter text-xs font-semibold uppercase tracking-wide', paidPanel.label)}>
            Usuário pago
          </p>
          <p className={cn('mt-1 font-inter text-lg font-bold', paidPanel.value)}>
            {PAID_REQUIREMENT_LABELS[department.paidRequired]}
          </p>
          {department.paidNote ? (
            <p className={cn('mt-2 font-inter text-xs leading-relaxed', paidPanel.note)}>
              {department.paidNote}
            </p>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function TicketExampleCard({
  example,
  isOpen,
  onToggle,
}: {
  example: TicketExample;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await copyText(example.message);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <article
      className={cn(
        'overflow-hidden rounded-2xl border bg-white transition-[border-color,box-shadow] duration-300',
        isOpen ? 'border-blue-200 shadow-md ring-1 ring-blue-100' : 'border-gray-200 shadow-sm'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50/80 md:px-6"
        aria-expanded={isOpen}
      >
        <div className="min-w-0 flex-1">
          <p className="mb-0.5 font-inter text-xs font-semibold uppercase tracking-wide text-blue-600">
            {example.department}
          </p>
          <h3 className="font-poppins text-base font-bold text-gray-900 md:text-lg">{example.occasion}</h3>
        </div>
        <ChevronDown
          className={cn(
            'h-5 w-5 shrink-0 text-gray-400 transition-transform duration-300',
            isOpen && 'rotate-180 text-blue-600'
          )}
          aria-hidden
        />
      </button>

      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t border-gray-100">
            <div className="grid gap-0 md:grid-cols-2 md:divide-x md:divide-gray-100">
              <div className="border-b border-gray-100 px-5 py-4 md:border-b-0 md:px-6 md:py-5">
                <p className="mb-1.5 font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Assunto
                </p>
                <p className="font-inter text-sm font-semibold text-gray-900">{example.subject}</p>
              </div>
              <div className="px-5 py-4 md:px-6 md:py-5">
                <p className="mb-1.5 font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Help Topic
                </p>
                <p className="font-inter text-sm text-gray-700">{example.department}</p>
              </div>
            </div>
            <div className="border-t border-gray-100 px-5 py-5 md:px-6">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Mensagem
                </p>
                <button
                  type="button"
                  onClick={handleCopy}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 font-inter text-xs font-semibold transition-colors',
                    copied
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                  )}
                  title="Copiar mensagem"
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5" aria-hidden />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                      Copiar
                    </>
                  )}
                </button>
              </div>
              <pre className="whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-4 font-inter text-sm leading-relaxed text-slate-800">
                {example.message}
              </pre>
            </div>
            <div className="border-t border-gray-100 bg-blue-50/50 px-5 py-4 md:px-6">
              <p className="font-inter text-sm leading-relaxed text-blue-900">
                <span className="font-semibold">Por que funciona: </span>
                {example.whyItWorks}
              </p>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function TicketExamplesList({ examples }: { examples: TicketExample[] }) {
  const [openId, setOpenId] = useState<string | null>(examples[0]?.id ?? null);

  return (
    <div className="flex flex-col gap-3">
      {examples.map((example) => (
        <TicketExampleCard
          key={example.id}
          example={example}
          isOpen={openId === example.id}
          onToggle={() => setOpenId(openId === example.id ? null : example.id)}
        />
      ))}
    </div>
  );
}

function PracticeCard({ card }: { card: TicketPracticeCard }) {
  const isGood = card.kind === 'good';
  return (
    <div
      className={cn(
        'rounded-2xl border p-4 shadow-sm',
        isGood
          ? 'border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white'
          : 'border-red-100 bg-gradient-to-br from-red-50/50 to-white'
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        {isGood ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
        ) : (
          <XCircle className="h-4 w-4 text-red-500" aria-hidden />
        )}
        <span
          className={cn(
            'font-inter text-xs font-bold uppercase tracking-wide',
            isGood ? 'text-emerald-700' : 'text-red-600'
          )}
        >
          {isGood ? 'Boa prática' : 'Evite'}
        </span>
      </div>
      <h4 className="mb-1.5 font-poppins text-sm font-bold text-gray-900">{card.title}</h4>
      <p className="font-inter text-sm leading-relaxed text-gray-600">{card.description}</p>
    </div>
  );
}

function FaqAccordion({ items }: { items: TicketFaqItem[] }) {
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const isOpen = openId === item.id;
        return (
          <div
            key={item.id}
            className={cn(
              'overflow-hidden rounded-2xl border bg-white transition-colors duration-200',
              isOpen ? 'border-blue-200 shadow-sm ring-1 ring-blue-100' : 'border-gray-200'
            )}
          >
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : item.id)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50/80"
              aria-expanded={isOpen}
            >
              <span className="flex min-w-0 items-start gap-3">
                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" aria-hidden />
                <span className="font-inter text-base font-semibold text-gray-900">{item.question}</span>
              </span>
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 text-gray-400 transition-transform duration-300',
                  isOpen && 'rotate-180 text-blue-600'
                )}
                aria-hidden
              />
            </button>
            <div
              className={cn(
                'grid transition-all duration-300 ease-in-out',
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              )}
            >
              <div className="overflow-hidden">
                <p className="border-t border-gray-100 px-5 py-4 pl-12 font-inter text-sm leading-relaxed text-gray-600">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function XatTicketsGuide() {
  const data = getXatTicketsGuideData();
  const goodPractices = data.practices.filter((p) => p.kind === 'good');
  const avoidPractices = data.practices.filter((p) => p.kind === 'avoid');

  return (
    <div className="space-y-16 not-prose">
      {/* Antes de abrir */}
      <section aria-labelledby="tickets-before-heading">
        <h2
          id="tickets-before-heading"
          className="mb-6 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
        >
          Antes de abrir um ticket
        </h2>
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50/40 p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="font-poppins text-lg font-bold text-emerald-900">Quando abrir um ticket</h3>
            </div>
            <ul className="space-y-2.5">
              {data.whenToOpen.map((item) => (
                <li key={item} className="flex gap-3 font-inter text-sm leading-relaxed text-emerald-900/90">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/30 p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <Ban className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="font-poppins text-lg font-bold text-amber-900">Quando NÃO abrir um ticket</h3>
            </div>
            <ul className="space-y-2.5">
              {data.whenNotToOpen.map((item) => (
                <li key={item} className="flex gap-3 font-inter text-sm leading-relaxed text-amber-900/90">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 rounded-xl border border-amber-100 bg-white/60 px-4 py-3 font-inter text-sm leading-relaxed text-amber-900/80">
              Nesses casos, prefira um chat de ajuda (Help), o fórum xat ou os artigos do xat Ajuda — você
              receberá resposta mais rápida.
            </p>
          </div>
        </div>
      </section>

      {/* Usuário pago */}
      <section
        aria-labelledby="tickets-paid-heading"
        className="rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 via-white to-sky-50/50 p-6 shadow-md md:p-8"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/25">
            <UserCheck className="h-6 w-6" aria-hidden />
          </div>
          <h2 id="tickets-paid-heading" className="font-poppins text-2xl font-bold text-gray-900 md:text-3xl">
            Quem pode abrir tickets?
          </h2>
        </div>
        <p className="max-w-3xl font-inter text-base leading-relaxed text-gray-700 md:text-lg">
          {data.paidUserHighlight}
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a
            href="https://xat.com/ticket"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-inter text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
          >
            Abrir ticket
            <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
          <a
            href="https://forum.xat.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-5 py-2.5 font-inter text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-50"
          >
            Fórum xat
            <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
        </div>
      </section>

      {/* Timeline */}
      <section aria-labelledby="tickets-flow-heading">
        <h2
          id="tickets-flow-heading"
          className="mb-2 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
        >
          Como criar um ticket
        </h2>
        <p className="mb-8 max-w-3xl font-inter text-base text-gray-600">
          Siga este fluxo em xat.com/ticket — cada etapa aumenta as chances de uma resposta rápida.
        </p>
        <ol className="relative space-y-0">
          {data.timeline.map((step, index) => (
            <li key={step.id} className="relative flex gap-5 pb-10 last:pb-0">
              {index < data.timeline.length - 1 ? (
                <span
                  className="absolute left-5 top-10 h-[calc(100%-2.5rem)] w-px bg-gradient-to-b from-blue-300 to-blue-100"
                  aria-hidden
                />
              ) : null}
              <div className="relative z-[1] flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 font-inter text-sm font-bold text-white shadow-md shadow-blue-600/30">
                {index + 1}
              </div>
              <div className="min-w-0 flex-1 pt-1">
                <h3 className="font-poppins text-lg font-bold text-gray-900">{step.title}</h3>
                <p className="mt-1.5 font-inter text-sm leading-relaxed text-gray-600 md:text-[15px]">
                  {step.description}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Departamentos */}
      <section aria-labelledby="tickets-departments-heading">
        <h2
          id="tickets-departments-heading"
          className="mb-2 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
        >
          Escolhendo o departamento
        </h2>
        <p className="mb-6 max-w-3xl font-inter text-base text-gray-600">
          Escolha o tópico correto no campo «Help Topic». Tickets no departamento errado podem demorar mais
          ou não ser respondidos.
        </p>
        <div className="mb-6 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-inter text-xs font-semibold text-emerald-800">
            <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
            Não — disponível sem compra
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 font-inter text-xs font-semibold text-amber-800">
            <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden />
            Sim — exige usuário pago
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-inter text-xs font-semibold text-blue-800">
            <span className="h-2 w-2 rounded-full bg-blue-500" aria-hidden />
            Depende — verifique a descrição
          </span>
        </div>
        <div className="flex flex-col gap-4">
          {data.departments.map((dept) => (
            <DepartmentCard key={dept.id} department={dept} />
          ))}
        </div>
      </section>

      {/* Boas práticas */}
      <section aria-labelledby="tickets-practices-heading">
        <h2
          id="tickets-practices-heading"
          className="mb-2 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
        >
          Como aumentar as chances de receber uma resposta rapidamente
        </h2>
        <p className="mb-8 max-w-3xl font-inter text-base text-gray-600">
          Tickets bem escritos em inglês, com detalhes completos, são atendidos com muito mais rapidez.
        </p>
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <h3 className="mb-4 flex items-center gap-2 font-poppins text-base font-bold text-emerald-800">
              <CheckCircle2 className="h-5 w-5" aria-hidden />
              Boa prática
            </h3>
            <div className="flex flex-col gap-3">
              {goodPractices.map((card) => (
                <PracticeCard key={card.id} card={card} />
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-4 flex items-center gap-2 font-poppins text-base font-bold text-red-700">
              <XCircle className="h-5 w-5" aria-hidden />
              Evite
            </h3>
            <div className="flex flex-col gap-3">
              {avoidPractices.map((card) => (
                <PracticeCard key={card.id} card={card} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Exemplos */}
      <section aria-labelledby="tickets-example-heading">
        <h2
          id="tickets-example-heading"
          className="mb-2 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
        >
          Exemplos de ticket por ocasião
        </h2>
        <p className="mb-8 max-w-3xl font-inter text-base text-gray-600">
          Cada situação pede um departamento e um nível de detalhe diferente. Use estes modelos em inglês
          como ponto de partida — adapte com seus dados reais.
        </p>
        <TicketExamplesList examples={data.exampleTickets} />
      </section>

      {/* Tempo de resposta */}
      <section aria-labelledby="tickets-response-heading">
        <div className="flex gap-4 rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50/40 p-6 shadow-sm md:p-8">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
            <Clock className="h-6 w-6" aria-hidden />
          </div>
          <div>
            <h2
              id="tickets-response-heading"
              className="mb-2 font-poppins text-xl font-bold text-gray-900 md:text-2xl"
            >
              Tempo de resposta
            </h2>
            <p className="font-inter text-sm leading-relaxed text-gray-700 md:text-base">
              {data.responseTime}
            </p>
          </div>
        </div>
      </section>

      {/* Acompanhar ticket */}
      {data.trackTicketSteps.length > 0 ? (
        <section aria-labelledby="tickets-track-heading">
          <h2
            id="tickets-track-heading"
            className="mb-6 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
          >
            Ver e responder a um ticket aberto
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {data.trackTicketSteps.map((step, index) => (
              <div
                key={step.title}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
              >
                <span className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 font-inter text-sm font-bold text-gray-700">
                  {index + 1}
                </span>
                <h3 className="mb-2 font-poppins text-base font-bold text-gray-900">{step.title}</h3>
                <p className="font-inter text-sm leading-relaxed text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
          {data.trackTicketNote ? (
            <p className="mt-4 font-inter text-sm leading-relaxed text-gray-600">{data.trackTicketNote}</p>
          ) : null}
        </section>
      ) : null}

      {/* FAQ */}
      <section aria-labelledby="tickets-faq-heading">
        <h2
          id="tickets-faq-heading"
          className="mb-6 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
        >
          Perguntas frequentes
        </h2>
        <FaqAccordion items={data.faq} />
      </section>

      {/* Dicas importantes */}
      <section aria-labelledby="tickets-tips-heading">
        <h2
          id="tickets-tips-heading"
          className="mb-2 font-poppins text-2xl font-bold text-gray-900 md:text-3xl"
        >
          Dicas importantes
        </h2>
        <p className="mb-6 max-w-3xl font-inter text-base text-gray-600">
          Regras essenciais antes e durante o envio do ticket — leia com atenção.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {data.callouts.map((callout, idx) => (
            <TicketTipCard key={`${callout.type}-${callout.title}-${idx}`} callout={callout} />
          ))}
        </div>
      </section>
    </div>
  );
}
